import axios from 'axios';
import type { BillingEnforcementPolicy } from '../schemas/billingEnforcement';
import { billingEnforcementPolicyResponseSchema } from '../schemas/billingEnforcement';
import { uiLogger } from '../utils/loggerInstance';
import { apiClient } from './apiClient';

export type {
  BillingEnforcementPolicy,
  BillingEnforcementState,
  BillingNudgeLevel,
  BillingUiAccessMode,
  BillingWriteMode,
} from '../schemas/billingEnforcement';

export const billingEnforcementService = {
  /**
   * Returns billing-enforcement policy for the authenticated organization member.
   * Null means: no organization context (**404** from API per `RequireOrgMember`) only.
   * Invalid response bodies throw so React Query surfaces an error and ProtectedRoute fails closed.
   * **403** is logged and rethrown so telemetry and React Query can surface authorization issues.
   */
  async getMyPolicy(): Promise<BillingEnforcementPolicy | null> {
    try {
      const response = await apiClient.get('/api/v1/billing-enforcement/me');
      const parsed = billingEnforcementPolicyResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        uiLogger.error('billingEnforcementService.getMyPolicy: response validation failed', {
          issues: parsed.error.issues,
          rawKeys:
            response.data && typeof response.data === 'object'
              ? Object.keys(response.data as object)
              : [],
        });
        throw new Error('Billing policy response validation failed');
      }
      return parsed.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 404) {
          uiLogger.debug('billingEnforcementService.getMyPolicy: no organization (404)', {
            detail: typeof data === 'object' && data !== null ? data : undefined,
          });
          return null;
        }
        if (status === 403) {
          uiLogger.error('billingEnforcementService.getMyPolicy: forbidden (403)', {
            detail: data,
            url: error.config?.url,
          });
          throw error;
        }
      }
      throw error;
    }
  },
};
